/**
 * Camera narrative generator — produces unique text content per camera
 * based on its specifications. Avoids the "thin content / near-duplicate"
 * SEO problem by varying narrative based on real spec differences.
 *
 * Used by /camera/:slug pages.
 */

import type { CameraPageData } from './cameraSlug';
import { getMegapixels, formatResolution, getCameraTypeName, getEffectiveRangeM } from './cameraSlug';

// ────────────────────────────────────────────────────────────────
// Bucketing helpers — categorize cameras by their specs
// ────────────────────────────────────────────────────────────────

export type ResolutionTier = 'sd' | 'hd' | 'fullhd' | 'qhd' | '5mp' | '4k' | '8k';
export type IRTier = 'none' | 'short' | 'medium' | 'long' | 'extreme';
export type FOVTier = 'narrow' | 'standard' | 'wide' | 'ultra-wide';
export type RangeTier = 'close' | 'medium' | 'long' | 'extreme';

export function getResolutionTier(mp: number): ResolutionTier {
  if (mp <= 1) return 'sd';
  if (mp <= 1.5) return 'hd';
  if (mp <= 2.5) return 'fullhd';
  if (mp <= 4.5) return 'qhd';
  // 5-6MP sensors are NOT 4K/8MP — a separate tier keeps the copy honest on
  // the ~3,800 five-to-six-MP SKUs that were previously mislabeled "4K (8MP)".
  if (mp <= 6.5) return '5mp';
  if (mp <= 9) return '4k';
  return '8k';
}

export function getIRTier(irRange: number): IRTier {
  if (irRange === 0) return 'none';
  if (irRange <= 20) return 'short';
  if (irRange <= 40) return 'medium';
  if (irRange <= 80) return 'long';
  return 'extreme';
}

export function getFOVTier(hfov: number): FOVTier {
  if (hfov < 50) return 'narrow';
  if (hfov < 80) return 'standard';
  if (hfov < 120) return 'wide';
  return 'ultra-wide';
}

export function getRangeTier(range: number): RangeTier {
  if (range <= 15) return 'close';
  if (range <= 35) return 'medium';
  if (range <= 60) return 'long';
  return 'extreme';
}

// ────────────────────────────────────────────────────────────────
// Narrative pieces — varied phrasings per spec combination
// ────────────────────────────────────────────────────────────────

const TYPE_DESCRIPTIONS: Record<string, string[]> = {
  bullet: [
    'rugged outdoor-grade bullet form factor designed to withstand exposure to weather, vandalism, and direct sunlight',
    'weather-sealed bullet housing optimized for perimeter, parking, and exterior building installations',
    'long-distance bullet body that mounts cleanly under eaves, on poles, or along fencelines',
  ],
  dome: [
    'low-profile dome enclosure that blends into ceilings, vandal-resistant by design and harder to tamper with than bullet cameras',
    'discreet dome housing well-suited to indoor retail, office, lobby, and educational environments where aesthetics matter',
    'tinted-bubble dome that obscures the lens direction, deterring tampering and vandalism',
  ],
  ptz: [
    'pan-tilt-zoom platform giving operators the ability to follow targets, scan large areas on auto-tour, and zoom in for identification',
    'motorized PTZ chassis ideal for large open spaces — parking lots, stadiums, airports, and city surveillance',
    'high-speed dome PTZ that can patrol preset positions on a schedule and react to motion or analytics events',
  ],
  panoramic: [
    'fisheye/panoramic optic that captures a complete 180° or 360° scene from a single ceiling or wall position',
    'multi-sensor panoramic body that replaces several traditional cameras with one seamless image',
    'wide-coverage panoramic camera that eliminates blind spots in lobbies, classrooms, retail floors, and warehouses',
  ],
};

const RESOLUTION_NARRATIVES: Record<ResolutionTier, string[]> = {
  sd: [
    'Sub-megapixel resolution is suitable only for general overview footage — useful for movement detection but insufficient for facial recognition or license-plate reading.',
  ],
  hd: [
    '720p HD resolution captures enough detail for basic identification at close range (under 8 meters) but loses face detail beyond that.',
  ],
  fullhd: [
    '1080p Full HD remains the most widely deployed CCTV resolution in 2026 — it balances bandwidth, storage, and detail well, supporting facial identification up to roughly 12 meters in good light.',
    'At 1080p the camera produces detail dense enough to identify faces inside ~10 meters and read European license plates inside 8 meters under daylight conditions.',
  ],
  qhd: [
    '4MP resolution delivers a noticeable detail upgrade over 1080p — facial identification extends to roughly 18 meters, and digital zoom on recorded footage stays usable.',
    '4MP sits in the sweet spot for installations where storage is a concern but you still want crisper retroactive zooming during incident review.',
  ],
  '5mp': [
    '5MP resolution sharpens the image well past 1080p — facial identification extends to roughly 20 meters and retroactive digital zoom on recorded footage stays crisp, without the storage and bandwidth cost of a full 4K stream.',
    'At 5-to-6MP the sensor sits between 1080p economy and 4K forensic detail: enough pixels-per-metre for evidence-grade faces at typical entrance and driveway distances, while keeping recording bitrate materially lighter than an 8MP camera.',
  ],
  '4k': [
    '4K (8MP) resolution roughly quadruples 1080p pixel density, pushing usable identification distance past 25 meters and giving forensic-grade digital zoom on archived footage.',
    'At 8MP the camera covers more area per device while preserving enough pixels-per-meter for legal evidence quality at typical installation distances.',
  ],
  '8k': [
    'Ultra-high resolution above 8MP is reserved for situations where one camera must replace several — large open atriums, parking decks, and stadium seating areas where digital zoom on a single feed substitutes for multiple cameras.',
  ],
};

const IR_NARRATIVES: Record<IRTier, string[]> = {
  none: [
    'There is no built-in IR illuminator on this model, so it depends entirely on ambient light. In dark environments, pair it with external IR floods or white-light illuminators.',
  ],
  short: [
    'A short-range IR illuminator (≤20 m) handles entrance vestibules, indoor corridors, and small enclosed spaces. Beyond 20 meters the IR fall-off causes faces to lose detail in pure darkness.',
  ],
  medium: [
    'Medium IR range (20–40 m) is appropriate for typical residential driveways, small commercial perimeters, and back-of-house loading bays where reach beyond ~30 m is rarely needed at night.',
  ],
  long: [
    'A 50–80 m IR range turns this into a true night-perimeter camera — open yards, fenced lots, and rural properties become practical use cases without supplemental lighting.',
    'With long-range IR you can rely on the camera as the only night-vision source over a wide area, eliminating the cost and visual clutter of dedicated IR floods.',
  ],
  extreme: [
    'An IR reach above 80 m is unusual for the format and is intended for highway monitoring, large industrial sites, and airport perimeters where conventional cameras would simply lose the target after dark.',
  ],
};

const FOV_NARRATIVES: Record<FOVTier, string[]> = {
  narrow: [
    'A narrow field of view trades coverage area for pixel density at distance — the right choice when you need to read faces or plates far from the camera, e.g., long corridors, gate approaches, ANPR installations.',
  ],
  standard: [
    'A standard 50–80° horizontal FOV mimics how a person sees a scene and works well for general-purpose monitoring of rooms, parking spaces, and perimeter walls without distortion.',
  ],
  wide: [
    'A wide 80–120° angle covers a large room or open area from a single mounting position, making it ideal for retail floors, conference rooms, and small parking lots.',
  ],
  'ultra-wide': [
    'An ultra-wide angle (>120°) captures nearly an entire scene from one position, but pixels per meter drops sharply at the edges, so identification at the periphery is limited.',
  ],
};

const SUPER_LOW_LIGHT_LINES = [
  'Super Low Light technology pulls usable color out of scenes lit by little more than a streetlight or moonlight, eliminating the need to drop into B/W mode in most night conditions.',
  'The Super Low Light sensor is the feature most installers care about for residential and parking-lot use — it preserves identification detail when older cameras lose all color and most contour.',
];

// ────────────────────────────────────────────────────────────────
// Use-case generation — varies based on spec combination
// ────────────────────────────────────────────────────────────────

export interface UseCaseSuggestion {
  title: string;
  description: string;
}

export function generateUseCases(camera: CameraPageData): UseCaseSuggestion[] {
  const mp = getMegapixels(camera.resolution);
  const resTier = getResolutionTier(mp);
  const irTier = getIRTier(camera.irRange);
  const fovTier = getFOVTier(camera.hfov);
  // Single source of "range" across the whole page: the DORI-derived effective
  // range (see getEffectiveRangeM) — never the raw catalog `range` placeholder,
  // which mirrors irRange on ~48% of SKUs and contradicts the coverage section.
  const effRange = getEffectiveRangeM(camera);
  const rangeTier = getRangeTier(effRange);
  const cases: UseCaseSuggestion[] = [];

  // Outdoor perimeter — needs long IR + decent resolution
  if ((irTier === 'long' || irTier === 'extreme') && (resTier === 'fullhd' || resTier === 'qhd' || resTier === '5mp' || resTier === '4k')) {
    cases.push({
      title: 'Outdoor perimeter & fenceline',
      description: `With ${camera.irRange}m IR reach and ${formatResolution(camera.resolution)} resolution, this camera covers fences, yards, and approach roads in complete darkness. Mount on poles or eaves at 4–6 m height angled slightly downward.`,
    });
  }

  // Parking lot — wide FOV + good range
  if ((fovTier === 'wide' || fovTier === 'standard') && (rangeTier === 'long' || rangeTier === 'extreme')) {
    cases.push({
      title: 'Parking lot & open yard',
      description: `The ${camera.hfov}° field of view spans roughly ${(2 * effRange * Math.tan(camera.hfov * Math.PI / 360)).toFixed(0)} meters at the camera's effective range — wide enough to monitor multiple parking rows from a single mounting point.`,
    });
  }

  // Entrance / lobby — wide FOV + close range or low-light
  if (fovTier === 'wide' || fovTier === 'ultra-wide' || camera.superLowLight) {
    cases.push({
      title: 'Entrance, lobby & vestibule',
      description: `${fovTier === 'wide' || fovTier === 'ultra-wide' ? `The ${camera.hfov}° field of view captures the entire entrance and surrounding waiting area from a single ceiling mount.` : 'Low-light sensitivity preserves face detail even when interior lighting is dimmed for energy savings.'} Useful for capturing visitors as they enter and leave.`,
    });
  }

  // Corridor / aisle — narrow FOV
  if (fovTier === 'narrow') {
    cases.push({
      title: 'Long corridor & aisle monitoring',
      description: `The ${camera.hfov}° narrow angle is intentionally tuned for corridor work — it concentrates pixel density along the length of the hallway rather than wasting resolution on the side walls. Mount at the far end facing along the corridor.`,
    });
  }

  // PTZ — large open area patrol
  if (camera.type === 'ptz') {
    cases.push({
      title: 'Large area patrol & target tracking',
      description: 'PTZ control enables operator-initiated tracking of suspicious activity, scheduled tours of preset positions overnight, and forensic zoom into recorded footage. Best paired with a wide-angle "overview" camera that triggers the PTZ via analytics.',
    });
  }

  // Panoramic — single-camera room replacement
  if (camera.type === 'panoramic') {
    cases.push({
      title: 'Single-camera room coverage',
      description: 'A single panoramic camera replaces 3–4 conventional cameras when ceiling-mounted in the center of an open space. Ideal for retail floors, classrooms, and small warehouses where simplification of cabling and licensing matters.',
    });
  }

  // Retail / office — dome + standard FOV + decent resolution
  if (camera.type === 'dome' && resTier !== 'sd' && resTier !== 'hd') {
    cases.push({
      title: 'Retail & office interior',
      description: `Dome form factor and ${formatResolution(camera.resolution)} resolution make this a clean retail/office choice — the housing blends into ceilings and the sensor delivers enough detail for POS-area monitoring and incident review.`,
    });
  }

  // ANPR — narrow FOV + high MP + good range
  if (fovTier === 'narrow' && (resTier === 'qhd' || resTier === '5mp' || resTier === '4k') && rangeTier !== 'close') {
    cases.push({
      title: 'License plate / ANPR',
      description: `The combination of ${camera.hfov}° narrow angle and ${formatResolution(camera.resolution)} resolution provides the pixel density required for ANPR at typical lane widths, especially when paired with proper lane lighting and perpendicular mounting.`,
    });
  }

  // Fallback if nothing matched (shouldn't happen often)
  if (cases.length === 0) {
    cases.push({
      title: 'General surveillance',
      description: effRange > 0
        ? `Suited to general-purpose monitoring tasks within ${effRange} meters of the mounting point with ${camera.hfov}° of horizontal coverage.`
        : `Suited to general-purpose monitoring tasks with ${camera.hfov}° of horizontal coverage.`,
    });
  }

  return cases.slice(0, 4);
}

// ────────────────────────────────────────────────────────────────
// Strengths & considerations — pros/cons varied by spec
// ────────────────────────────────────────────────────────────────

export function generateStrengths(camera: CameraPageData): string[] {
  const mp = getMegapixels(camera.resolution);
  const effRange = getEffectiveRangeM(camera);
  const strengths: string[] = [];

  if (mp >= 4) strengths.push(`${mp}MP sensor preserves detail under digital zoom on recorded footage`);
  if (camera.irRange >= 50) strengths.push(`${camera.irRange}m IR range eliminates the need for external night-time illuminators`);
  if (camera.superLowLight) strengths.push('Super Low Light sensor maintains color detail in near-darkness');
  if (camera.hfov >= 90) strengths.push(`${camera.hfov}° wide field of view reduces the number of cameras needed per area`);
  if (camera.type === 'ptz') strengths.push('PTZ control enables operator-driven tracking and pre-set tours');
  if (camera.type === 'dome') strengths.push('Vandal-resistant dome housing harder to tamper with than bullet form factor');
  if (camera.type === 'panoramic') strengths.push('Single-camera coverage of full rooms reduces cabling and licensing cost');
  if (effRange >= 30) strengths.push(`${effRange}m effective range supports outdoor perimeter and parking-lot deployments`);

  return strengths.slice(0, 5);
}

export function generateConsiderations(camera: CameraPageData): string[] {
  const mp = getMegapixels(camera.resolution);
  const fovTier = getFOVTier(camera.hfov);
  const considerations: string[] = [];

  if (mp > 0 && mp <= 2) considerations.push('Resolution limits forensic digital zoom — recorded faces beyond ~12m may not be identifiable');
  if (camera.irRange === 0) considerations.push('No built-in IR — requires external illumination for night use');
  if (camera.irRange > 0 && camera.irRange <= 20) considerations.push('Short IR range limits night usability to indoor or close-proximity scenarios');
  if (fovTier === 'narrow') considerations.push('Narrow angle requires more cameras to cover wide areas — plan camera count accordingly');
  if (fovTier === 'ultra-wide') considerations.push('Edge distortion and reduced peripheral pixel density limit identification at the corners of the frame');
  if (camera.type === 'ptz') considerations.push('PTZ cameras only see one direction at a time — pair with a fixed overview camera to avoid blind spots');
  if (mp >= 8) considerations.push('Higher resolution increases storage and bandwidth requirements proportionally — verify NVR capacity');
  if (camera.type === 'panoramic') considerations.push('Fisheye distortion requires dewarping in software for natural viewing angles');

  if (considerations.length === 0) {
    considerations.push('No notable limitations for the intended class of installations');
  }

  return considerations.slice(0, 4);
}

// ────────────────────────────────────────────────────────────────
// Long-form intro paragraph (unique per camera)
// ────────────────────────────────────────────────────────────────

function pickByHash(arr: string[], seed: string): string {
  // Deterministic pseudo-random pick based on slug — same camera always gets same text
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  }
  return arr[Math.abs(hash) % arr.length];
}

export function generateIntroParagraph(camera: CameraPageData): string {
  const mp = getMegapixels(camera.resolution);
  const resTier = getResolutionTier(mp);
  const irTier = getIRTier(camera.irRange);
  const fovTier = getFOVTier(camera.hfov);

  const typeDesc = pickByHash(TYPE_DESCRIPTIONS[camera.type] || TYPE_DESCRIPTIONS.bullet, camera.slug);
  const resDesc = pickByHash(RESOLUTION_NARRATIVES[resTier], camera.slug + 'r');
  const irDesc = pickByHash(IR_NARRATIVES[irTier], camera.slug + 'i');
  const fovDesc = pickByHash(FOV_NARRATIVES[fovTier], camera.slug + 'f');
  const lowLight = camera.superLowLight ? ' ' + pickByHash(SUPER_LOW_LIGHT_LINES, camera.slug + 'l') : '';

  return `The ${camera.manufacturer} ${camera.model} is a ${typeDesc}. ${resDesc} ${irDesc} ${fovDesc}${lowLight}`;
}

// ────────────────────────────────────────────────────────────────
// Installation tips — varied per type + FOV + range
// ────────────────────────────────────────────────────────────────

export function generateInstallationTips(camera: CameraPageData): string[] {
  const tips: string[] = [];
  const mp = getMegapixels(camera.resolution);
  const fovTier = getFOVTier(camera.hfov);
  const effRange = getEffectiveRangeM(camera);

  // Mounting height
  if (camera.type === 'bullet' && effRange >= 30) {
    tips.push(`Mount at 4–6 m height angled 10–20° downward — high enough to deter tampering, low enough to preserve face detail at ${Math.round(effRange * 0.7)}m.`);
  } else if (camera.type === 'dome') {
    tips.push('Ceiling-mount at 2.5–3.5 m for indoor use; for vandal-prone outdoor locations use a wall mount with the lens facing outward and downward.');
  } else if (camera.type === 'ptz') {
    tips.push('Mount above 4 m on a rigid pole or wall bracket — PTZ vibrations are amplified at low mounting heights and degrade image quality during pan operations.');
  } else if (camera.type === 'panoramic') {
    tips.push('Center-ceiling mount at 2.7–3.5 m gives the most uniform coverage; corner mounts work but waste roughly 25% of the field of view on the wall.');
  }

  // Lighting / night
  if (camera.irRange > 0 && camera.irRange < 30) {
    tips.push('Pair with low-cost ambient lighting (motion-triggered LED floods) to extend usable identification distance beyond the IR limit.');
  } else if (camera.irRange >= 50) {
    tips.push('Avoid mounting under direct ceiling lights or behind glass — both reflect IR back into the lens and create severe glare in the image.');
  }

  // FOV-specific positioning
  if (fovTier === 'wide' || fovTier === 'ultra-wide') {
    tips.push('Position so the most important subject (entrance, cash register, gate) is in the center third of the frame — peripheral pixel density drops with wide angles.');
  } else if (fovTier === 'narrow') {
    tips.push(`Plan cabling for multiple cameras to cover wide areas — at ${camera.hfov}° you cover roughly ${(2 * 10 * Math.tan(camera.hfov * Math.PI / 360)).toFixed(0)} m of width per camera at 10 m distance.`);
  }

  // Storage planning
  if (mp >= 4) {
    tips.push(`Plan storage at roughly ${Math.round(mp * 4)} GB per camera per day for continuous H.265 recording at 15fps — verify NVR has sufficient HDD bay capacity.`);
  }

  return tips.slice(0, 4);
}

// ────────────────────────────────────────────────────────────────
// Coverage math — concrete computed numbers per camera (not boilerplate).
// Adds 50-80 unique words per page based on real geometry.
// ────────────────────────────────────────────────────────────────

/** EN 62676-4:2025 thresholds in pixels per metre. */
const DORI_PX_PER_M = { detect: 25, observe: 62.5, recognize: 125, identify: 250 } as const;

/** Cover-width at distance, given a horizontal FOV in degrees. */
function widthAt(distanceM: number, hfovDeg: number): number {
  return 2 * distanceM * Math.tan((hfovDeg * Math.PI) / 360);
}

/** Maximum distance at which the camera meets a DORI threshold. */
function dori(camera: CameraPageData, threshold: number): number {
  // The declared WxH beats any estimate: a 4:3 sensor run through the 16:9
  // shortcut gains ~15% phantom width (2560x1920 = 4.9MP → estimated 2952 px
  // vs real 2560), which overstated every DORI distance on the page against
  // the designer's engine for the same camera. The simulator's 1:1 rule
  // applies to the SEO pages too — same camera, same numbers everywhere.
  const wxh = (camera.resolution || '').match(/^(\d+)\s*[x×]\s*\d+$/i);
  const horizPx = wxh
    ? parseInt(wxh[1], 10)
    // Fallback for MP-only strings, assuming 16:9:
    // mp = w·h ≈ w² · (9/16)   →   w ≈ √(mp · 16/9 · 1e6)
    : Math.round(Math.sqrt((getMegapixels(camera.resolution) || 2) * (16 / 9) * 1_000_000));
  // distance at which (px / width-at-distance) === threshold
  // → distance = horizPx / (2 · threshold · tan(hfov/2))
  const tanHalf = Math.tan((camera.hfov * Math.PI) / 360);
  if (tanHalf <= 0) return 0;
  return horizPx / (2 * threshold * tanHalf);
}

export interface CoverageMath {
  paragraph: string;
  doriDistances: { detect: number; observe: number; recognize: number; identify: number };
  widthAt5m: number;
  widthAt10m: number;
  widthAt20m: number;
}

export function generateCoverageMath(camera: CameraPageData): CoverageMath {
  const widthAt5m = widthAt(5, camera.hfov);
  const widthAt10m = widthAt(10, camera.hfov);
  const widthAt20m = widthAt(20, camera.hfov);
  const detect = Math.round(dori(camera, DORI_PX_PER_M.detect));
  const observe = Math.round(dori(camera, DORI_PX_PER_M.observe));
  const recognize = Math.round(dori(camera, DORI_PX_PER_M.recognize));
  const identify = Math.round(dori(camera, DORI_PX_PER_M.identify));

  const paragraph =
    `At a 5-metre standoff the ${camera.manufacturer} ${camera.model} covers about ` +
    `${widthAt5m.toFixed(1)} m of scene width; at 10 m that grows to ${widthAt10m.toFixed(1)} m, ` +
    `and at 20 m to ${widthAt20m.toFixed(1)} m. Translating those numbers through ` +
    `EN 62676-4 pixel-density thresholds, the camera can detect a person up to ` +
    `${detect} m, observe up to ${observe} m, recognise up to ${recognize} m, and ` +
    `produce identification-grade detail (the ≥250 px/m bar used by Polish and ` +
    `EU courts as evidentiary) up to ${identify} m. Effective range above the ` +
    `identify threshold relies on supplementary lighting or a longer-focal-length ` +
    `optic — the geometry above assumes the ${camera.focalLength || 'fixed'} lens declared by the manufacturer.`;

  return {
    paragraph,
    doriDistances: { detect, observe, recognize, identify },
    widthAt5m,
    widthAt10m,
    widthAt20m,
  };
}

// ────────────────────────────────────────────────────────────────
// Power & cabling — PoE class + Cat6 distance limits.
// Adds 40-60 unique words per page; concrete numbers per type.
// ────────────────────────────────────────────────────────────────

export interface PowerCablingNote {
  poeClass: string;
  estimatedWatts: number;
  cat6MaxDistanceM: number;
  paragraph: string;
}

export function generatePowerCabling(camera: CameraPageData): PowerCablingNote {
  // Watts heuristic per type. PTZ + heaters dominate; bullet/dome with IR
  // sit in 802.3at; passive dome / bullet without IR usually af.
  let watts = 5.5;
  if (camera.type === 'ptz') watts = camera.irRange >= 50 ? 60 : 30;
  else if (camera.type === 'panoramic') watts = 12;
  else if (camera.irRange >= 50) watts = 12;
  else if (camera.irRange > 0) watts = 7.5;

  let poeClass = '802.3af (Class 3, ≤15.4 W)';
  if (watts > 60) poeClass = '802.3bt Type 4 (Class 8, ≤90 W)';
  else if (watts > 30) poeClass = '802.3bt Type 3 (Class 5–6, ≤60 W)';
  else if (watts > 15.4) poeClass = '802.3at (Class 4, ≤30 W)';

  // Cat6 official max = 100 m (90 m horizontal + 10 m patches). For PoE++
  // (bt) high-current paths, voltage drop becomes a concern past ~80 m on
  // smaller-gauge runs.
  const cat6MaxDistanceM = watts > 30 ? 80 : 100;

  const paragraph =
    `The ${camera.manufacturer} ${camera.model} draws an estimated ${watts.toFixed(1)} W ` +
    `under typical load (with the IR illuminator engaged where applicable), placing it in the ` +
    `${poeClass} budget. A single switch port supplies enough power without an injector. ` +
    `Cabling: stick to Cat6 STP for runs up to ${cat6MaxDistanceM} m end-to-end — beyond that, ` +
    `add an inline PoE extender or move to fibre with a media converter at the camera end. ` +
    `On outdoor runs, route through ø25 PVC or PE conduit to keep the cable shielded from UV ` +
    `and rodents.`;

  return { poeClass, estimatedWatts: watts, cat6MaxDistanceM, paragraph };
}

// ────────────────────────────────────────────────────────────────
// Privacy & legal — RODO/GDPR Art. 13 obligations specific to the
// install scenario each camera type implies. Adds 50-70 unique words.
// ────────────────────────────────────────────────────────────────

export function generatePrivacyConsiderations(camera: CameraPageData): string {
  const typeName = getCameraTypeName(camera.type).toLowerCase();
  const effRange = getEffectiveRangeM(camera);
  const wide = camera.hfov >= 90;
  const longRange = effRange >= 30;

  const baseNote =
    `Any camera operating in a publicly accessible area falls under GDPR (RODO in ` +
    `Poland) — Art. 13 obliges the data controller to post an information sign at ` +
    `every entry point with controller name, DPO contact, lawful basis (typically ` +
    `Art. 6(1)(f) legitimate interest for property protection), purpose, and retention period.`;

  let typeNote = '';
  if (camera.type === 'ptz') {
    typeNote =
      ` The ${camera.manufacturer} ${camera.model}'s PTZ pan range can sweep into ` +
      `neighbouring properties or public roads; document allowed pan limits in the ` +
      `installation record and configure privacy masks (see ${camera.manufacturer}'s ` +
      `web-UI under "Privacy Mask") for any window or doorway you cannot legally surveil.`;
  } else if (camera.type === 'panoramic') {
    typeNote =
      ` Panoramic / fisheye coverage almost always captures more than the controller ` +
      `intended — schedule a privacy mask review after install to blur out staff-only ` +
      `areas, neighbouring premises and any glass that admits a view of third parties.`;
  } else if (wide && longRange) {
    typeNote =
      ` With ${camera.hfov}° horizontal FOV and ${effRange} m effective range, ` +
      `a single ${typeName} unit covers a large swath of scene — confirm during ` +
      `commissioning that no part of the visible area extends beyond the property ` +
      `boundary, and add a privacy mask if it does.`;
  } else {
    typeNote =
      ` Even a narrowly-aimed ${typeName} can incidentally record passers-by, so ` +
      `the Art. 13 sign at the entry point applies regardless of how tightly the ` +
      `lens is framed.`;
  }

  return baseNote + typeNote;
}

// ────────────────────────────────────────────────────────────────
// Extended FAQ entries — feeds the FAQPage schema with deeper questions
// per camera, bumping word count and answering long-tail queries.
// ────────────────────────────────────────────────────────────────

export interface FaqEntry {
  question: string;
  answer: string;
}

export function generateExtendedFAQ(camera: CameraPageData): FaqEntry[] {
  const power = generatePowerCabling(camera);
  const coverage = generateCoverageMath(camera);
  const mp = getMegapixels(camera.resolution);

  // Daily storage estimate at 15 fps H.265 — same heuristic as
  // generateInstallationTips so numbers stay consistent across the page.
  const gbPerDay = Math.max(2, Math.round(mp * 4));
  const monthGB = gbPerDay * 30;

  const out: FaqEntry[] = [
    {
      question: `Is the ${camera.manufacturer} ${camera.model} suitable for outdoor installation?`,
      answer:
        camera.type === 'panoramic'
          ? `Most panoramic units in the ${camera.manufacturer} catalogue are IP66/IK10 rated for outdoor use — verify the exact suffix in the model code (e.g. "-W" for weather-rated). Mount under an eave or recessed canopy where possible: direct rainfall on the dome causes droplets that distort the wide-angle image.`
          : camera.type === 'ptz'
          ? `${camera.manufacturer} PTZ models are generally designed for outdoor use with IP66 ingress rating and -30°C to +60°C operating range. Mount on a rigid pole at ≥ 4 m to dampen wind-driven vibration that would degrade the live image during pan operations.`
          : `Yes for the IP66/IP67-rated variant of this model. Bullet and dome bodies in the ${camera.manufacturer} range survive direct rain, snow and UV; check the IP / IK rating against your environment (IP66 for rain/dust, IK10 for vandal-prone locations).`,
    },
    {
      question: `What PoE class powers the ${camera.manufacturer} ${camera.model}?`,
      answer:
        `The camera draws roughly ${power.estimatedWatts.toFixed(1)} W in steady-state operation, requiring a ${power.poeClass} switch port. ` +
        `A single Cat6 STP cable carries both data and power up to ${power.cat6MaxDistanceM} m — beyond that, add an inline extender or convert to fibre near the camera.`,
    },
    {
      question: `How much storage does the ${camera.manufacturer} ${camera.model} consume per day?`,
      answer:
        `At ${formatResolution(camera.resolution)} resolution, 15 fps H.265 continuous recording, the ${camera.model} produces approximately ${gbPerDay} GB per day — about ${monthGB} GB per month. ` +
        `Motion-only or event-triggered recording typically cuts that by 40–70 % depending on scene activity. Plan NVR HDD capacity for the longest retention period required by your privacy policy or insurance contract.`,
    },
    {
      question: `Up to what distance can the ${camera.manufacturer} ${camera.model} identify a person?`,
      answer:
        `Per EN 62676-4:2025 pixel-density thresholds, this camera identifies (≥ 250 px/m on the target) up to about ${coverage.doriDistances.identify} m, ` +
        `recognises a person up to ${coverage.doriDistances.recognize} m, observes general activity up to ${coverage.doriDistances.observe} m, ` +
        `and detects motion up to ${coverage.doriDistances.detect} m. Identification distance assumes daylight or full IR illumination on the target — at the limits of the IR cone the effective identify distance shrinks roughly 30 %.`,
    },
    {
      question: `What network bandwidth does ${camera.manufacturer} ${camera.model} use?`,
      answer:
        `Typical ingest is ${Math.max(2, Math.round(mp * 1.5))} Mbps for the main stream (H.265, medium scene complexity, 15 fps) plus ~0.5 Mbps for a low-resolution sub-stream used by mobile clients. ` +
        `For a 16-camera installation of this class, plan a 50–100 Mbps NVR uplink and a dedicated VLAN for camera traffic.`,
    },
  ];
  // no resolution read from the maker: storage, bandwidth and DORI distances would be figures for a sensor we do not know
  return mp > 0 ? out : out.slice(0, 2);
}

// ────────────────────────────────────────────────────────────────
// Installer cost & timeline — concrete labour estimate per camera
// type. Adds another 30-50 unique words and a real number that
// contractors and prosumers both look for.
// ────────────────────────────────────────────────────────────────

export function generateInstallerCostNote(camera: CameraPageData): string {
  // Per-camera labour estimate (mounting + termination + commission).
  let hours = 3;
  if (camera.type === 'ptz') hours = 5;
  else if (camera.type === 'panoramic') hours = 4;
  else if (getEffectiveRangeM(camera) >= 50) hours = 4; // long-range bullet usually = high mount

  // EU contractor average rate ~€45/h (2024-2025).
  const labourEur = hours * 45;

  return (
    `A typical EU installer quotes ${hours}–${hours + 1} h of labour per ${getCameraTypeName(camera.type).toLowerCase()} ` +
    `of this class (approximately €${labourEur}–€${labourEur + 50} excluding hardware), broken down as ` +
    `bracket mounting, cable termination at both ends, on-site commissioning, and a brief operator handover. ` +
    `Allow extra time for high mounts (>4 m), exterior installations requiring scaffolding, or sites where ` +
    `cable routing demands wall penetrations or conduit work.`
  );
}
